import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from "react-simple-maps";
import { feature } from "topojson-client";
import topology from "../../content/world-110m.json";
import { MAP_MARKERS, MAP_ARCS } from "../../content/site";
import { prefersReducedMotion } from "../../lib/motion";

/**
 * WorldMap — restrained institutional world map.
 * Phase 6: pulsing markers + drawn arcs (US→Luxembourg, US→Dubai) on scroll-in.
 * Respects prefers-reduced-motion.
 */
export const WorldMap = ({ preview = false }) => {
  const [hovered, setHovered] = useState(null);
  const [inView, setInView] = useState(false);
  const wrapRef = useRef(null);
  const reduce = prefersReducedMotion();

  const geoData = useMemo(
    () => feature(topology, topology.objects.countries),
    [],
  );

  useEffect(() => {
    if (reduce || preview) {
      // If reduced motion or preview mode, show final state immediately without animation
      setInView(true);
      return;
    }
    const node = wrapRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.35 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [reduce, preview]);

  return (
    <div className="relative w-full" ref={wrapRef} data-testid="world-map">
      <ComposableMap
        projection="geoNaturalEarth1"
        projectionConfig={{ scale: 165 }}
        width={980}
        height={460}
        style={{ width: "100%", height: "auto", background: "transparent" }}
      >
        {/* Aged-brass glow filter for the HQ marker (soft SVG blur). */}
        <defs>
          <filter id="hq-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <Geographies geography={geoData}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                style={{
                  default: {
                    fill: "#F7F8FA",
                    stroke: "rgba(15,19,42,0.15)",
                    strokeWidth: 0.5,
                    outline: "none",
                  },
                  hover: {
                    fill: "#F7F8FA",
                    stroke: "rgba(15,19,42,0.15)",
                    strokeWidth: 0.5,
                    outline: "none",
                  },
                  pressed: {
                    fill: "#F7F8FA",
                    stroke: "rgba(15,19,42,0.15)",
                    strokeWidth: 0.5,
                    outline: "none",
                  },
                }}
              />
            ))
          }
        </Geographies>

        {/* Arcs — drawn on scroll-in. Suppressed in preview mode. */}
        {!preview
          ? MAP_ARCS.map((arc, i) => (
              <Line
                key={arc.id}
                from={arc.from}
                to={arc.to}
                stroke="#B8A26A"
                strokeWidth={0.9}
                strokeLinecap="round"
                fill="none"
                data-testid={`map-arc-${arc.id}`}
                style={{
                  strokeDasharray: 600,
                  strokeDashoffset: inView ? 0 : 600,
                  opacity: inView ? 0.85 : 0,
                  transition: reduce
                    ? "none"
                    : `stroke-dashoffset 1400ms cubic-bezier(0.22, 1, 0.36, 1) ${
                        400 + i * 300
                      }ms, opacity 600ms ease ${400 + i * 300}ms`,
                }}
              />
            ))
          : null}

        {MAP_MARKERS.map((m) => {
          const isHq = m.kind === "hq";
          const radius = isHq ? 6.5 : 4.5;
          const pulseColor = isHq ? "#B8A26A" : "#0B132B";
          return (
            <Marker key={m.id} coordinates={m.coordinates}>
              <g
                role="button"
                tabIndex={0}
                aria-label={`${m.name} — ${m.role}`}
                data-testid={`map-marker-${m.id}`}
                onMouseEnter={() => setHovered(m.id)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(m.id)}
                onBlur={() => setHovered(null)}
                style={{ cursor: "default", outline: "none" }}
              >
                {/* Pulse ring — SMIL animation. Suppressed in preview mode and for reduced motion. */}
                {!reduce && !preview ? (
                  <circle
                    r={radius}
                    fill="none"
                    stroke={pulseColor}
                    strokeWidth={1}
                    opacity={0.5}
                    style={{ pointerEvents: "none" }}
                  >
                    <animate
                      attributeName="r"
                      from={radius}
                      to={radius * 3.2}
                      dur={isHq ? "2.6s" : "3.2s"}
                      begin={isHq ? "0s" : "1.1s"}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      from="0.5"
                      to="0"
                      dur={isHq ? "2.6s" : "3.2s"}
                      begin={isHq ? "0s" : "1.1s"}
                      repeatCount="indefinite"
                    />
                  </circle>
                ) : null}

                {/* Invisible larger hit target */}
                <circle r={14} fill="transparent" />
                {isHq ? (
                  <g filter="url(#hq-glow)">
                    <circle
                      r={radius}
                      fill="#B8A26A"
                      stroke="#0B132B"
                      strokeWidth={1}
                    >
                      {!reduce && !preview ? (
                        <animate
                          attributeName="opacity"
                          values="0.85;1;0.85"
                          dur="3.4s"
                          repeatCount="indefinite"
                        />
                      ) : null}
                    </circle>
                  </g>
                ) : (
                  <circle
                    r={radius}
                    fill="#0B132B"
                    stroke="#F7F8FA"
                    strokeWidth={0.75}
                  />
                )}
                {hovered === m.id ? (
                  <g
                    style={{
                      transition: "opacity 200ms ease",
                      pointerEvents: "none",
                    }}
                  >
                    <rect
                      x={10}
                      y={-32}
                      width={Math.max(110, m.name.length * 7 + 22)}
                      height={42}
                      fill="#F7F8FA"
                      stroke="rgba(15,19,42,0.25)"
                      strokeWidth={0.75}
                    />
                    <text
                      x={20}
                      y={-19}
                      fill="#B8A26A"
                      fontFamily="Inter, sans-serif"
                      fontSize={7}
                      letterSpacing={1.3}
                      style={{ textTransform: "uppercase" }}
                    >
                      {m.role.toUpperCase()}
                    </text>
                    <text
                      x={20}
                      y={-5}
                      fill="#0B132B"
                      fontFamily="Cormorant Garamond, serif"
                      fontSize={13}
                    >
                      {m.name}
                    </text>
                  </g>
                ) : null}
              </g>
            </Marker>
          );
        })}
      </ComposableMap>

      {/* Legend — hidden in preview mode */}
      {!preview ? (
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 font-sans text-[11px] tracking-eyebrow uppercase text-navy/70">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-full bg-gold ring-1 ring-navy"
            />
            <span>Headquarters</span>
          </div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 rounded-full bg-navy"
            />
            <span>Strategic Presence</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WorldMap;
